import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useChat } from '../hooks/useChat';
import { playSound, playFor, getSoundFor } from '../utils/notificationSounds';

const ChatContext = createContext(null);

/* ── Chat toast (reused from Layout.js) ── */
function ChatToast({ t: toastInstance, senderName, preview, onClick }) {
  // Self-dismiss after 5s. react-hot-toast's internal timer is unreliable for
  // custom toasts in v2.6.x, so we manage the timeout ourselves.
  useEffect(() => {
    const id = toastInstance.id;
    const timer = setTimeout(() => toast.dismiss(id), 5000);
    return () => clearTimeout(timer);
  }, [toastInstance.id]);
  return (
    <div
      className={`presence-toast ${toastInstance.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
      style={{ borderLeftColor: '#3b82f6', cursor: 'pointer', position: 'relative' }}
      onClick={() => { onClick(); toast.dismiss(toastInstance.id); }}
    >
      <button style={{
        position: 'absolute', top: 4, right: 4, background: 'none', border: 'none',
        cursor: 'pointer', padding: 2, borderRadius: 4, color: 'var(--c-text-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }} onClick={(e) => { e.stopPropagation(); toast.dismiss(toastInstance.id); }}><X size={14} /></button>
      <div style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>💬</div>
      <div className="presence-toast-body">
        <span className="presence-toast-name">{senderName}</span>
        <span className="presence-toast-status">{preview}</span>
      </div>
    </div>
  );
}

/* ── Sticker grant toast ── */
const STICKER_RARITY_COLOR = {
  bronze: '#b45309', silver: '#94a3b8', golden: '#f59e0b', platinum: '#0ea5e9',
};
const CONFETTI_COLORS = ['#3b82f6', '#a855f7', '#f59e0b', '#10b981', '#ef4444', '#ec4899'];

function ConfettiBurst({ count = 14 }) {
  // 14 absolutely-positioned squares with random angle/distance via CSS vars.
  const pieces = Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + (Math.random() * 0.4 - 0.2);
    const dist = 36 + Math.random() * 28;
    const cx = Math.cos(angle) * dist;
    const cy = Math.sin(angle) * dist - 20; // bias upward
    const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    return (
      <span
        key={i}
        className="confetti-piece"
        style={{
          background: color,
          left: '50%', top: '50%',
          ['--cx']: `${cx}px`,
          ['--cy']: `${cy}px`,
          animationDelay: `${Math.random() * 80}ms`,
        }}
      />
    );
  });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
      {pieces}
    </div>
  );
}

function StickerToast({ t: toastInstance, sticker, levelUp, count }) {
  const color = STICKER_RARITY_COLOR[sticker.rarity] || STICKER_RARITY_COLOR.bronze;
  // Self-dismiss after 8s — see ChatToast comment.
  useEffect(() => {
    const id = toastInstance.id;
    const timer = setTimeout(() => toast.dismiss(id), 8000);
    return () => clearTimeout(timer);
  }, [toastInstance.id]);
  return (
    <div
      className={`presence-toast ${toastInstance.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
      style={{
        borderLeftColor: color,
        position: 'relative',
        minWidth: 300,
        background: `linear-gradient(135deg, ${color}14 0%, var(--c-surface) 60%)`,
        overflow: 'visible',
      }}
    >
      <ConfettiBurst />
      <button style={{
        position: 'absolute', top: 4, right: 4, background: 'none', border: 'none',
        cursor: 'pointer', padding: 2, borderRadius: 4, color: 'var(--c-text-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
      }} onClick={() => toast.dismiss(toastInstance.id)}><X size={14} /></button>
      <div style={{ position: 'relative', zIndex: 1, fontSize: 36, lineHeight: 1, flexShrink: 0, color }}>
        <span className="sticker-toast-emoji">{sticker.emoji || '🎖️'}</span>
      </div>
      <div className="presence-toast-body" style={{ position: 'relative', zIndex: 1 }}>
        <span className="presence-toast-name" style={{ color, fontWeight: 700 }}>
          {levelUp ? `${sticker.name} — Nivel ×${count}` : `🎫 ${sticker.name}`}
        </span>
        <span className="presence-toast-status">
          {sticker.description}
          {sticker.unlocks_pokemon ? ` · 🔓 ${sticker.unlocks_pokemon}` : ''}
        </span>
      </div>
    </div>
  );
}

const EMPTY = {
  socket: null, connected: false, socketRef: { current: null },
  conversations: [], loadingConversations: false,
  activeConversationId: null, selectConversation: () => {},
  messages: [], loadingMessages: false, hasMoreMessages: false, loadMore: () => {},
  typingUsers: {}, onlineUsers: new Set(), userStatuses: {},
  sendMessage: () => {}, editMessage: () => {}, deleteMessage: () => {},
  startTyping: () => {}, stopTyping: () => {}, markAsRead: () => {},
  toggleReaction: () => {}, createConversation: () => {},
  uploadFile: () => {}, searchMessages: () => {},
  setChatStatus: () => {}, createTicketThread: () => {},
  leaveConversation: () => {}, deleteConversation: () => {},
  removeMember: () => {}, addMembers: () => {}, loadConversations: () => {},
  totalUnread: 0,
  // Floating panel state
  floatingOpen: false, setFloatingOpen: () => {},
  openFloatingConversation: () => {},
  chatNotifications: [],
};

export function ChatProvider({ children }) {
  const { user, hasModule, loadUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const chatEnabled = hasModule('chat');

  // Single useChat instance
  const chat = useChat(chatEnabled ? user : null);

  // Floating panel state
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [floatingConvId, setFloatingConvId] = useState(null);

  // Notification state (moved from Layout.js)
  const [chatNotifications, setChatNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem('chat_notifications') || '[]'); } catch { return []; }
  });

  const isOnChatPage = location.pathname === '/chat';
  const isOnChatPageRef = useRef(isOnChatPage);
  isOnChatPageRef.current = isOnChatPage;
  const floatingOpenRef = useRef(floatingOpen);
  floatingOpenRef.current = floatingOpen;
  const activeConvIdRef = useRef(chat.activeConversationId);
  activeConvIdRef.current = chat.activeConversationId;

  // Open floating panel to a specific conversation
  const openFloatingConversation = useCallback((convId) => {
    if (isOnChatPageRef.current) {
      // If on chat page, just select the conversation there
      chat.selectConversation(convId);
      return;
    }
    setFloatingConvId(convId);
    setFloatingOpen(true);
    chat.selectConversation(convId);
  }, [chat]);

  // Listen for conversation:updated for toasts (when not on /chat and panel is closed)
  useEffect(() => {
    if (!chatEnabled || !chat.socketRef.current) return;
    const s = chat.socketRef.current;

    const handleConvUpdated = ({ conversationId, lastMessage }) => {
      // Skip if on chat page or if floating panel is open showing this conversation
      if (isOnChatPageRef.current) return;
      if (floatingOpenRef.current && activeConvIdRef.current === conversationId) return;

      if (lastMessage && lastMessage.sender_id !== user.id) {
        const senderName = lastMessage.sender_first_name || lastMessage.sender_name || '';
        const preview = lastMessage.is_deleted ? '' : (lastMessage.content?.slice(0, 60) || '');

        // Add to bell dropdown notifications
        setChatNotifications(prev => {
          const existing = prev.findIndex(n => n.conversationId === conversationId);
          let updated;
          if (existing !== -1) {
            const entry = { ...prev[existing], senderName, preview, time: lastMessage.created_at || new Date().toISOString() };
            updated = [entry, ...prev.filter((_, i) => i !== existing)];
          } else {
            updated = [
              { id: `${conversationId}-${Date.now()}`, conversationId, senderName, preview, time: lastMessage.created_at || new Date().toISOString() },
              ...prev,
            ];
          }
          updated = updated.slice(0, 20);
          localStorage.setItem('chat_notifications', JSON.stringify(updated));
          return updated;
        });

        // Toast → opens floating panel
        toast.custom((ti) => (
          <ChatToast t={ti} senderName={senderName} preview={preview}
            onClick={() => openFloatingConversation(conversationId)} />
        ), { duration: 5000 });

        playFor('chat');
      }
    };

    const handleMention = ({ conversationId, from, preview }) => {
      if (isOnChatPageRef.current) return;
      toast.custom((ti) => (
        <ChatToast t={ti} senderName={`@${from}`} preview={preview?.slice(0, 60) || ''}
          onClick={() => openFloatingConversation(conversationId)} />
      ), { duration: 5000 });
    };

    const handleStickerGranted = ({ sticker, count, level_up }) => {
      const toastsOn = user?.stickerToastsEnabled !== false;
      if (toastsOn) {
        // Achievement-style toast — sticky for 8s so the user actually notices
        toast.custom((ti) => (
          <StickerToast t={ti} sticker={sticker} levelUp={level_up} count={count} />
        ), { duration: 8000 });
        // Sonido por rareza; el bronce cae en el sonido general del usuario. Los otros
        // tres son deliberadamente escalonados (es una mecánica de juego) y no se tocan.
        // El fallback lee el sistema nuevo: la clave vieja `notif_sound` ya no la escribe
        // nadie, así que un usuario nuevo la tendría vacía y esto quedaría clavado en el
        // default de fábrica sin que nadie lo note.
        const fallback = getSoundFor('system');
        const RARITY_SOUND = {
          bronze: fallback,
          silver: 'win98-notify',
          golden: 'win95-chime',
          platinum: 'win98-tada',
        };
        const userOff = fallback === 'none';
        playSound(userOff ? 'none' : (RARITY_SOUND[sticker.rarity] || fallback));
      }
      // Always refresh /auth/me so pokemonLocked + stickerCount update
      loadUser();
      // Tell the wallet to refetch
      window.dispatchEvent(new Event('sticker:granted'));
    };

    s.on('conversation:updated', handleConvUpdated);
    s.on('mention:new', handleMention);
    s.on('sticker:granted', handleStickerGranted);
    return () => {
      s.off('conversation:updated', handleConvUpdated);
      s.off('mention:new', handleMention);
      s.off('sticker:granted', handleStickerGranted);
    };
  }, [chatEnabled, chat.socketRef.current, user?.id, openFloatingConversation, loadUser]); // eslint-disable-line

  // Reset chat notifications when navigating to chat page
  useEffect(() => {
    if (isOnChatPage) {
      setChatNotifications([]);
      localStorage.removeItem('chat_notifications');
    }
  }, [isOnChatPage]);

  const clearChatNotifications = useCallback(() => {
    setChatNotifications([]);
    localStorage.removeItem('chat_notifications');
  }, []);

  if (!chatEnabled) {
    return <ChatContext.Provider value={EMPTY}>{children}</ChatContext.Provider>;
  }

  const value = {
    ...chat,
    floatingOpen, setFloatingOpen,
    floatingConvId, setFloatingConvId,
    openFloatingConversation,
    chatNotifications, clearChatNotifications,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) return EMPTY;
  return ctx;
}
